<template>
  <div class="bg-gradient-to-b from-white to-[#d5eef6] border-t border-code-medical-center-secondary flex flex-col items-center justify-center py-0 w-full">
    <div class="max-w-[1200px] w-full mx-auto px-4 md:px-0">
      <div class="flex flex-col gap-12 items-start overflow-hidden px-0 py-12 w-full lg:gap-16 lg:py-16">
        <!-- Main Content Row -->
        <div class="flex flex-col gap-[32px] md:flex-row items-start w-full">
          <!-- Left Section: Logo and Navigation Links -->
          <div class="flex flex-col gap-[32px] items-start w-full md:w-[584px]">
            <div class="h-[48px] w-[138px] relative">
              <img
                alt="Logo"
                class="absolute inset-0 max-w-none object-cover pointer-events-none w-full h-full"
                :src="logoImage"
              >
            </div>
            <div class="flex flex-wrap gap-[24px] items-center w-full">
              <component
                :is="link.url ? 'a' : 'span'"
                v-for="(link, index) in visibleNavigationLinks"
                :key="index"
                :href="link.url || undefined"
                class="font-sans font-medium leading-[24px] text-dark text-[16px] whitespace-nowrap"
                :class="link.url ? 'hover:underline' : ''"
              >
                {{ link.label }}
              </component>
            </div>
          </div>

          <!-- Right Section: Contact Information -->
          <div class="flex flex-col gap-3 items-start w-full md:w-[584px]">
            <div class="flex flex-col items-start w-full md:w-[438px]">
              <p class="font-sans font-medium leading-10 text-[32px] tracking-[-0.64px] lg:leading-[44px] lg:text-[36px] lg:tracking-[-0.72px]">
                {{ contactHeading }}
              </p>
            </div>
            <div class="flex flex-col items-start">
              <!-- Location -->
              <div class="flex gap-2 items-start overflow-hidden px-0 py-3.5 rounded-lg">
                <div class="overflow-hidden relative w-5 h-5 shrink-0">
                  <img
                    alt="Location icon"
                    class="block max-w-none w-full h-full"
                    :src="locationIcon"
                  >
                </div>
                <p class="font-sans font-medium leading-[18px] text-[16px]">
                  <span class="block text-dark">{{ businessName }}</span>
                  <span class="block text-gray">{{ locationAddress }}</span>
                </p>
              </div>

              <!-- Phone -->
              <ContactPhoneRow wrap class="min-h-12 h-auto px-0">
                <p class="font-sans font-medium leading-[18px] text-[16px]">
                  <span class="block text-dark">
                    {{ phoneLabel }}&nbsp;<a
                      v-if="phoneNumber"
                      :href="`tel:${phoneNumber}`"
                      class="font-semibold text-link no-underline"
                    >{{ phoneNumber }}</a>
                  </span>
                  <span v-if="phoneHours" class="block text-gray">{{ phoneHours }}</span>
                </p>
              </ContactPhoneRow>

              <!-- Email -->
              <ContactEmailRow wrap class="min-h-12 h-auto px-0">
                <p class="font-sans font-medium leading-[18px] text-[16px]">
                  <span class="text-dark">{{ emailLabel }}&nbsp;</span>
                  <a
                    v-if="emailAddress"
                    :href="`mailto:${emailAddress}`"
                    class="font-semibold text-link no-underline"
                  >{{ emailAddress }}</a>
                </p>
              </ContactEmailRow>
            </div>

            <!-- Contact Button -->
            <a
              v-if="contactButtonText"
              :href="contactButtonUrl || '#'"
              class="border border-[#262626] flex gap-2 h-12 w-full items-center justify-center overflow-hidden px-5 py-3.5 rounded-lg shadow-[0px_2px_6px_0px_rgba(16,24,40,0.06)] transition-colors hover:bg-gray-50 md:w-auto"
            >
              <div class="flex flex-col justify-center leading-0 text-[16px] text-center whitespace-nowrap font-sans font-semibold">
                <p class="leading-[18px]">{{ contactButtonText }}</p>
              </div>
              <div class="overflow-hidden relative w-[20px] h-[20px]">
                <div class="flex items-center justify-center w-full h-full">
                  <svg viewBox="0 0 10 5" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-[10px] h-[5px] rotate-[270deg]">
                    <path d="M1 1L5 4L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
              </div>
            </a>
          </div>
        </div>

        <!-- Bottom Section: Copyright and Policy Links -->
        <div class="border-t border-code-medical-center-secondary flex flex-col items-center gap-8 pb-0 pt-8 px-0 w-full md:flex-row md:h-[52px] md:items-center md:justify-between md:gap-4 md:text-left">
          <p class="w-full text-center font-sans font-normal leading-5 text-gray text-[14px] md:w-auto md:text-left">
            <span
              v-for="(line, index) in copyrightLines"
              :key="index"
              class="block md:inline"
            >
              <template v-if="index > 0" />{{ line }}
            </span>
          </p>
          <div class="flex w-full flex-col items-center gap-6 font-sans font-normal leading-5 text-gray text-[14px] md:w-auto md:flex-row md:flex-wrap md:gap-8">
            <component
              :is="link.url ? 'a' : 'span'"
              v-for="(link, index) in visiblePolicyLinks"
              :key="index"
              :href="link.url || undefined"
              class="text-center md:text-left"
              :class="link.url ? 'hover:underline' : ''"
            >
              {{ link.label }}
            </component>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import locationIcon from '@/images/components/location-marker.svg'
import logoImage from '@/images/components/cmslogo-new.png'
import ContactPhoneRow from '@/Shared/ContactPhoneRow.vue'
import ContactEmailRow from '@/Shared/ContactEmailRow.vue'

const props = defineProps({
  // Navigation links: each is a { label, url } pair. `*Url` props are hydrated
  // server-side from the matching `*PageId` page_link by BlockPageLinkHydrator.
  medicalConsultationsLabel: { type: String, default: '' },
  medicalConsultationsUrl:   { type: String, default: '' },
  proceduresLabel:           { type: String, default: '' },
  proceduresUrl:             { type: String, default: '' },
  examinationsLabel:         { type: String, default: '' },
  examinationsUrl:           { type: String, default: '' },
  specialistsLabel:          { type: String, default: '' },
  specialistsUrl:            { type: String, default: '' },
  specializationsLabel:      { type: String, default: '' },
  specializationsUrl:        { type: String, default: '' },
  newsLabel:                 { type: String, default: '' },
  newsUrl:                   { type: String, default: '' },
  infertilityClinicLabel:    { type: String, default: '' },
  infertilityClinicUrl:      { type: String, default: '' },
  familyHealthClinicLabel:   { type: String, default: '' },
  familyHealthClinicUrl:     { type: String, default: '' },
  genomicsInstituteLabel:    { type: String, default: '' },
  genomicsInstituteUrl:      { type: String, default: '' },

  // Contact info
  contactHeading:   { type: String, default: '' },
  businessName:     { type: String, default: '' },
  locationAddress:  { type: String, default: '' },
  phoneLabel:       { type: String, default: '' },
  phoneNumber:      { type: String, default: '' },
  phoneHours:       { type: String, default: '' },
  emailLabel:       { type: String, default: '' },
  emailAddress:     { type: String, default: '' },
  contactButtonText:{ type: String, default: '' },
  contactButtonUrl: { type: String, default: '' },

  // Bottom row
  copyrightText:      { type: String, default: '' },
  privacyPolicyLabel: { type: String, default: '' },
  privacyPolicyUrl:   { type: String, default: '' },
  cookiesPolicyLabel: { type: String, default: '' },
  cookiesPolicyUrl:   { type: String, default: '' },
  termsOfServiceLabel:{ type: String, default: '' },
  termsOfServiceUrl:  { type: String, default: '' },

  // eslint-disable-next-line vue/prop-name-casing
  site_context: { type: String, default: null },
})

// Build {label, url} pairs and drop any without a label.
const visibleNavigationLinks = computed(() => [
  { label: props.medicalConsultationsLabel, url: props.medicalConsultationsUrl },
  { label: props.proceduresLabel,           url: props.proceduresUrl },
  { label: props.examinationsLabel,         url: props.examinationsUrl },
  { label: props.specialistsLabel,          url: props.specialistsUrl },
  { label: props.specializationsLabel,      url: props.specializationsUrl },
  { label: props.newsLabel,                 url: props.newsUrl },
  { label: props.infertilityClinicLabel,    url: props.infertilityClinicUrl },
  { label: props.familyHealthClinicLabel,   url: props.familyHealthClinicUrl },
  { label: props.genomicsInstituteLabel,    url: props.genomicsInstituteUrl },
].filter((link) => link.label && link.label.trim() !== ''))

const visiblePolicyLinks = computed(() => [
  { label: props.privacyPolicyLabel,  url: props.privacyPolicyUrl },
  { label: props.cookiesPolicyLabel,  url: props.cookiesPolicyUrl },
  { label: props.termsOfServiceLabel, url: props.termsOfServiceUrl },
].filter((link) => link.label && link.label.trim() !== ''))

const copyrightLines = computed(() => {
  const text = props.copyrightText?.trim();
  if (!text) return [];

  const splitAt = text.indexOf('. ');
  if (splitAt === -1) return [text];

  return [text.slice(0, splitAt + 1), text.slice(splitAt + 2)];
})
</script>

<style scoped>
/* Component-specific styles */
</style>
